/**
 * #270 — a row loaded through the lean list API has no `linkPreview.thumbnail` (it carries
 * `linkPreview.thumbnailOmitted`). The send path must put the real thumbnail back before sending,
 * otherwise `resolveSendThunkLinkPreview` takes the lean preview "verbatim" and the delivered
 * message goes out without its preview image.
 *
 * Decision logic only, so it is unit-testable without a store or the network. FAILS OPEN: a missing
 * or failed lookup never blocks a send — a lean row sending without its image beats not sending.
 */
import type { UserMessage } from '@bt-common/schemas/bt/user-message';

type PreviewShape = { thumbnail?: unknown; thumbnailOmitted?: boolean } | null | undefined;

export const needsFullDocForSend = (m: UserMessage): boolean => {
    const lean = m as UserMessage & { hasThumbnail?: boolean };
    return lean.hasThumbnail === true || (m.linkPreview as PreviewShape)?.thumbnailOmitted === true;
};

export type FullDocDecision =
    | { action: 'send'; userMessage: UserMessage; fetched: boolean }
    | { action: 'skip'; reason: 'gone' | 'deleted' };

/**
 * @param fetchById resolves the live docs for the id (`[]` = confirmed gone), or null/undefined when
 *                  the lookup was unavailable (fail open).
 */
export const resolveFullDocForSend = async (
    userMessage: UserMessage,
    fetchById: (id: string) => Promise<UserMessage[] | null | undefined>,
): Promise<FullDocDecision> => {
    if (!needsFullDocForSend(userMessage)) return { action: 'send', userMessage, fetched: false };

    let docs: UserMessage[] | null | undefined;
    try {
        docs = await fetchById(String(userMessage._id));
    } catch (err) {
        console.warn(`[send-time full doc] lookup failed — sending the lean row, messageId: ${userMessage._id}`, err);
        return { action: 'send', userMessage, fetched: false };
    }
    if (!Array.isArray(docs)) {
        console.warn(`[send-time full doc] lookup unavailable — sending the lean row, messageId: ${userMessage._id}`);
        return { action: 'send', userMessage, fetched: false };
    }
    if (docs.length === 0) return { action: 'skip', reason: 'gone' };
    const full = docs[0];
    if (full.deleted === true) return { action: 'skip', reason: 'deleted' };

    // Take ONLY the preview from the stored doc: every other send field stays as the caller passed it.
    return { action: 'send', userMessage: { ...userMessage, linkPreview: full.linkPreview }, fetched: true };
};
